import React, { useRef, useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Button, Container, Grid, IconButton, Typography } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ThunderstormIcon from "@mui/icons-material/Thunderstorm";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import DescriptionIcon from "@mui/icons-material/Description";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HistoryEduIcon from "@mui/icons-material/HistoryEdu";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import StaticPageLayout from "../../components/StaticPageLayout";
import { QuoteModal } from "../../components/QuoteModal";
import { SavingsWidget } from "../../components/SavingsWidget";
import * as styles from "./styles";

type TranslateFnT = (key: string, options?: Record<string, unknown>) => string;

const videoThumb1 = "/img/assets/Video-thumb-1.jpg";
const videoThumb2 = "/img/assets/Video-thumb-2.jpg";
const videoThumb3 = "/img/assets/Video-thumb-3.jpg";

type ProductSpecT = {
  number?: string;
  unit?: string;
  label: string;
  description: string;
  icon?: React.ReactNode;
};

type ProductSlideT = {
  eyebrow: string;
  productName: string;
  headlineWhite: string;
  headlineGradient: string;
  headlineBreak?: boolean;
  body: string;
  imagePath: string;
  specs: ProductSpecT[];
};

type VideoTestimonialT = {
  id: number;
  label: string;
  description: string;
  placeholder: string;
  vimeoUrl?: string;
};

type TechnologyFeatureT = {
  title: string;
  description: string;
};

type MousePositionT = {
  x: number;
  y: number;
};

type InViewResultT = {
  ref: React.RefObject<HTMLDivElement>;
  inView: boolean;
};

const useInView = (threshold = 0.15): InViewResultT => {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setInView(true);
      },
      { threshold },
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, inView };
};

const PROPOSAL_IMAGES = [
  "/img/assets/Proposal-1.png",
  "/img/assets/Proposal-2.png",
  "/img/assets/Proposal-3.png",
  "/img/assets/Proposal-4.png",
];

type VideoHeroPropsT = {
  onScrollToProducts: () => void;
  t: TranslateFnT;
};

const VideoHero = ({ onScrollToProducts, t }: VideoHeroPropsT) => {
  const { user } = useParams<{ user?: string }>();
  const prefix = user ? `/${user}` : "";
  const [mousePos, setMousePos] = useState<MousePositionT>({ x: 0, y: 0 });

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const x = ((e.clientX - rect.left - centerX) / centerX) * 6;
    const y = ((e.clientY - rect.top - centerY) / centerY) * 6;
    setMousePos({ x, y });
  };

  const handleMouseLeave = () => {
    setMousePos({ x: 0, y: 0 });
  };

  return (
    <Box onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave} sx={styles.heroWrapper}>
      <Box
        component="video"
        autoPlay
        loop
        muted
        playsInline
        src="/img/assets/Powur-Hero.mp4"
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          width: "120%",
          height: "120%",
          objectFit: "cover",
          zIndex: 0,
          transform: `translate(-50%, -50%) translateX(${mousePos.x}px) translateY(${mousePos.y}px)`,
          transition: "transform 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
        }}
      />
      <Box sx={styles.heroVideoOverlay} />
      <Box sx={styles.heroRadialOverlay} />
      <Box sx={styles.heroTopGradient} />
      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 3 }}>
        <Box sx={{ textAlign: "center", mx: "auto" }}>
          <Typography sx={styles.heroEyebrow}>{t("hero.eyebrow")}</Typography>
          <Typography variant="h1" sx={styles.heroHeadlineBase}>
            {t("hero.headlineBase")}
          </Typography>
          <Typography variant="h1" sx={styles.heroHeadlineGradient}>
            {t("hero.headlineGradient")}
          </Typography>
          <Typography sx={styles.heroBody}>{t("hero.body")}</Typography>
          <Box sx={styles.heroButtonsWrapper}>
            <Button
              component={Link}
              to={`${prefix}/join`}
              variant="contained"
              size="large"
              color="primary"
              sx={styles.heroGetQuoteButton}
            >
              {t("hero.getQuoteLabel")}
            </Button>
            <Button size="large" onClick={onScrollToProducts} sx={styles.heroExploreButton}>
              {t("hero.exploreProductsLabel")} <KeyboardArrowDownIcon sx={{ ml: 0.5, fontSize: "1.2rem" }} />
            </Button>
          </Box>
        </Box>
      </Container>
      <Box onClick={onScrollToProducts} sx={styles.heroScrollIndicator}>
        <Typography sx={styles.heroScrollLabel}>{t("hero.scrollLabel")}</Typography>
        <KeyboardArrowDownIcon sx={{ fontSize: "1.4rem", color: "rgba(255,255,255,0.5)" }} />
      </Box>
      <Box sx={styles.heroBottomGradient} />
    </Box>
  );
};

type ProductsSectionPropsT = {
  sectionRef: React.RefObject<HTMLDivElement | null>;
  productSlides: ProductSlideT[];
};

const ProductsSection = ({ sectionRef, productSlides }: ProductsSectionPropsT) => {
  const { ref, inView } = useInView();
  const [activeIndex, setActiveIndex] = useState(0);
  const [visible, setVisible] = useState(true);
  const totalSlides = productSlides.length;
  const slideKeys = Array.from({ length: totalSlides }, (_, idx) => `slide-dot-${idx}`);

  const goTo = (index: number) => {
    if (index === activeIndex) return;
    setVisible(false);
    setTimeout(() => {
      setActiveIndex(index);
      setVisible(true);
    }, 300);
  };

  const slide = productSlides[activeIndex];

  return (
    <>
      <svg style={{ width: 0, height: 0, position: "absolute", overflow: "hidden" }}>
        <defs>
          <linearGradient id="specIconGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#3b82f6" />
            <stop offset="60%" stopColor="#38bdf8" />
            <stop offset="100%" stopColor="#67e8f9" />
          </linearGradient>
        </defs>
      </svg>
      <Box ref={sectionRef} sx={styles.productsSectionWrapper}>
        <Box sx={styles.productsDivider} />
        <Box sx={styles.productsContent}>
          <Container maxWidth="xl" sx={{ px: { xs: 3, md: 8 } }}>
            <div ref={ref}>
              <Box
                sx={{
                  opacity: visible ? 1 : 0,
                  transition: "opacity 0.3s ease",
                }}
              >
                <Grid container spacing={{ xs: 6, md: 10 }} alignItems="center">
                  <Grid key={activeIndex} item xs={12} md={6}>
                    <Typography sx={inView ? styles.productsEyebrow : styles.productsEyebrowNoAnim}>
                      {slide.eyebrow}
                    </Typography>
                    <Typography variant="h1" sx={inView ? styles.productsHeadline : styles.productsHeadlineNoAnim}>
                      <Box component="span" sx={{ color: "#0A0A0A" }}>
                        {slide.headlineWhite}
                      </Box>
                      {slide.headlineBreak && <br />}
                      <Box component="span" sx={{ ...styles.gradientText }}>
                        {slide.headlineGradient}
                      </Box>
                    </Typography>
                    <Typography sx={inView ? styles.productsBody : styles.productsBodyNoAnim}>{slide.body}</Typography>
                    <Grid container spacing={0} sx={inView ? styles.productsSpecsGrid : styles.productsSpecsGridNoAnim}>
                      {slide.specs.map((spec) => (
                        <Grid key={spec.label} item xs={12} sm={4}>
                          <Box sx={styles.productsSpecItem}>
                            {spec.number && (
                              <Typography sx={styles.productsSpecNumber}>
                                {spec.number}
                                <Box
                                  component="span"
                                  sx={{ fontSize: { xs: "1.4rem", md: "1.6rem" }, fontWeight: 700, ml: 0.5 }}
                                >
                                  {spec.unit}
                                </Box>
                              </Typography>
                            )}
                            {!spec.number && spec.icon && (
                              <Box sx={{ mb: 0.75, "& path": { fill: "url(#specIconGradient)" } }}>{spec.icon}</Box>
                            )}
                            <Typography sx={styles.productsSpecLabel}>{spec.label}</Typography>
                            <Typography sx={styles.productsSpecDescription}>{spec.description}</Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box sx={styles.productsImageContainer}>
                      <Box sx={styles.productsImageGlow} />
                      <Box component="img" src={slide.imagePath} alt={slide.productName} sx={styles.productsImage} />
                    </Box>
                  </Grid>
                </Grid>
              </Box>
              <Box sx={styles.productsNavWrapper}>
                <IconButton
                  onClick={() => goTo(activeIndex - 1)}
                  disabled={activeIndex === 0}
                  size="small"
                  sx={{
                    ...styles.productsNavButtonPrev,
                    color: activeIndex === 0 ? "rgba(0,0,0,0.2)" : "rgba(0,0,0,0.5)",
                    borderColor: activeIndex === 0 ? "rgba(0,0,0,0.08)" : "rgba(0,0,0,0.15)",
                  }}
                >
                  <ArrowBackIosNewIcon sx={{ fontSize: "0.9rem" }} />
                </IconButton>
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  {slideKeys.map((key, dotIdx) => (
                    <Box
                      key={key}
                      onClick={() => goTo(dotIdx)}
                      sx={{
                        width: dotIdx === activeIndex ? 24 : 8,
                        height: 8,
                        borderRadius: "4px",
                        bgcolor: dotIdx === activeIndex ? "#3b82f6" : "rgba(0,0,0,0.15)",
                        cursor: "pointer",
                        transition: "width 0.3s ease, background-color 0.3s ease",
                        "&:hover": {
                          bgcolor: dotIdx === activeIndex ? "#3b82f6" : "rgba(0,0,0,0.3)",
                        },
                      }}
                    />
                  ))}
                </Box>
                <IconButton
                  onClick={() => goTo(activeIndex + 1)}
                  disabled={activeIndex === totalSlides - 1}
                  size="small"
                  sx={{
                    ...styles.productsNavButtonNext,
                    color: activeIndex === totalSlides - 1 ? "rgba(0,0,0,0.2)" : "rgba(0,0,0,0.5)",
                    borderColor: activeIndex === totalSlides - 1 ? "rgba(0,0,0,0.08)" : "rgba(0,0,0,0.15)",
                  }}
                >
                  <ArrowForwardIosIcon sx={{ fontSize: "0.9rem" }} />
                </IconButton>
              </Box>
            </div>
          </Container>
        </Box>
      </Box>
    </>
  );
};

type StatCardT = {
  value: string;
  label: string;
  animationDelay: string;
};

type SocialProofSectionPropsT = {
  statCards: StatCardT[];
  t: TranslateFnT;
};

const SocialProofSection = ({ statCards, t }: SocialProofSectionPropsT) => {
  const { ref, inView } = useInView();
  return (
    <Box sx={styles.socialProofWrapper}>
      <Box
        aria-hidden="true"
        component="img"
        src="/img/assets/Symbol.png"
        sx={{
          ...styles.socialProofSymbol,
          transform: inView ? "translate(-50%, -50%) scale(1)" : "translate(-50%, -50%) scale(0.92)",
        }}
      />
      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <div ref={ref}>
          <Box sx={{ textAlign: "center" }}>
            <Typography sx={inView ? styles.socialProofEyebrow : styles.socialProofEyebrowNoAnim}>
              {t("socialProof.eyebrow")}
            </Typography>
            <Typography sx={{ ...(inView ? styles.socialProofHeadline : styles.socialProofHeadlineNoAnim) }}>
              <Box component="span" sx={styles.gradientText}>
                {t("socialProof.headlineGradient")}
              </Box>
              {t("socialProof.headlineBase")}
            </Typography>
            <Typography sx={inView ? styles.socialProofSubheading : styles.socialProofSubheadingNoAnim}>
              {t("socialProof.body")}
            </Typography>
            <Grid container spacing={3} justifyContent="center">
              {statCards.map((card) => (
                <Grid key={card.label} item xs={12} md={4}>
                  <Box
                    sx={{
                      ...styles.statCard,
                      animation: inView ? "cardEntrance 0.65s cubic-bezier(0.22, 1, 0.36, 1) forwards" : "none",
                      animationDelay: card.animationDelay,
                    }}
                  >
                    <Box sx={styles.statCardTopBorder} />
                    <Typography sx={styles.statCardValue}>{card.value}</Typography>
                    <Typography sx={styles.statCardLabel}>{card.label}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </div>
      </Container>
    </Box>
  );
};

type FinancingSectionPropsT = {
  t: TranslateFnT;
};

const FinancingSection = ({ t }: FinancingSectionPropsT) => {
  const { user } = useParams<{ user?: string }>();
  const prefix = user ? `/${user}` : "";
  const { ref, inView } = useInView();
  return (
    <Box sx={styles.financingSectionWrapper}>
      <Box sx={styles.financingDivider} />
      <Container maxWidth="lg">
        <div
          ref={ref}
          style={{
            opacity: inView ? 1 : 0,
            transform: inView ? "translateY(0)" : "translateY(32px)",
            transition: "opacity 0.8s ease, transform 0.8s ease",
          }}
        >
          <Typography align="center" sx={styles.financingEyebrow}>
            {t("financing.eyebrow")}
          </Typography>
          <Typography variant="h1" align="center" sx={styles.financingHeadline}>
            <Box component="span" sx={styles.gradientText}>
              {t("financing.headlineGradient")}
            </Box>
            {t("financing.headlineBase")}
          </Typography>
          <Typography align="center" sx={styles.financingBody}>
            {t("financing.body")}
          </Typography>
          <SavingsWidget />
          <Box sx={{ textAlign: "center", mt: 6 }}>
            <Typography sx={styles.financingNote}>{t("financing.finePrint")}</Typography>
            <Button
              component={Link}
              to={`${prefix}/join`}
              variant="contained"
              size="large"
              color="primary"
              sx={{ borderRadius: "50px", px: 4, py: 1.5 }}
            >
              {t("financing.ctaLabel")}
            </Button>
          </Box>
        </div>
      </Container>
    </Box>
  );
};

type TechnologySectionPropsT = {
  technologyFeatures: TechnologyFeatureT[];
  t: TranslateFnT;
};

const TechnologySection = ({ technologyFeatures, t }: TechnologySectionPropsT) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const frameRefs = useRef<(HTMLDivElement | null)[]>([]);
  const rafRef = useRef<number>(0);
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setEntered(true);
          observer.disconnect();
        }
      },
      { threshold: 0.05 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      rafRef.current = requestAnimationFrame(() => {
        const container = containerRef.current;
        if (!container) return;
        const rect = container.getBoundingClientRect();
        const scrollRange = container.offsetHeight - window.innerHeight;
        if (scrollRange <= 0) return;
        const scrollT = Math.max(0, Math.min(1, -rect.top / scrollRange)) * 3;
        frameRefs.current.forEach((el, i) => {
          if (!el) return;
          const offset = scrollT - i;
          const dist = Math.abs(offset);
          const xShift = offset * -160;
          const yShift = offset * 40;
          const scale = Math.max(0.65, 1 - dist * 0.14);
          const opacity = Math.max(0, 1 - dist * 1.8);
          const { style } = el;
          style.opacity = String(opacity);
          style.transform = `translate(calc(-50% + ${xShift}px), calc(-50% + ${yShift}px)) scale(${scale})`;
        });
      });
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => {
      window.removeEventListener("scroll", handleScroll);
      cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <Box ref={containerRef} sx={{ height: "400vh", position: "relative" }}>
      <Box sx={styles.techStickyPanel}>
        <Box
          sx={{
            ...styles.techCopyPanel,
            opacity: entered ? 1 : 0,
            transform: entered ? "translateY(0)" : "translateY(32px)",
            transition: "opacity 0.8s ease, transform 0.8s ease",
          }}
        >
          <Box sx={{ maxWidth: 560 }}>
            <Typography sx={styles.techEyebrow}>{t("technology.eyebrow")}</Typography>
            <Typography variant="h2" sx={styles.techHeadline}>
              <Box component="span" sx={{ color: "#0A0A0A" }}>
                {t("technology.headlineBase1")}
              </Box>
              <br />
              <Box component="span" sx={{ color: "#0A0A0A" }}>
                {t("technology.headlineBase2")}
              </Box>
              <Box component="span" sx={styles.gradientText}>
                {t("technology.headlineGradient")}
              </Box>
            </Typography>
            <Typography sx={styles.techBody}>{t("technology.body")}</Typography>
            <Box component="ul" sx={styles.techFeatureList}>
              {technologyFeatures.map((feature) => (
                <Box key={feature.title} component="li" sx={styles.techFeatureItem}>
                  <Box sx={styles.techFeatureCheckbox}>
                    <CheckIcon sx={{ fontSize: 13, color: "#3b82f6" }} />
                  </Box>
                  <Box>
                    <Typography sx={styles.techFeatureTitle}>{feature.title}</Typography>
                    <Typography sx={styles.techFeatureDescription}>{feature.description}</Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        <Box sx={styles.techImagesPanel}>
          <Box sx={styles.techImagesGlow} />
          {PROPOSAL_IMAGES.map((src, i) => (
            <Box
              key={src}
              ref={(el) => {
                frameRefs.current[i] = el as HTMLDivElement | null;
              }}
              sx={{
                ...styles.techProposalFrame,
                opacity: i === 0 ? 1 : 0,
              }}
              style={{ transform: "translate(-50%, -50%)" }}
            >
              <Box component="img" src={src} alt="" sx={styles.techProposalImage} />
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

type TimelineStepT = {
  number: string;
  title: string;
  description: string;
  icon: React.ReactNode;
};

type HowItWorksSectionPropsT = {
  timelineSteps: TimelineStepT[];
  t: TranslateFnT;
  onQuoteOpen: (zip: string) => void;
};

const HowItWorksSection = ({ timelineSteps, t, onQuoteOpen }: HowItWorksSectionPropsT) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [zip, setZip] = useState("");

  const handleZipSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (zip.trim().length !== 5) return;
    onQuoteOpen(zip.trim());
  };

  const handlePrev = () => setActiveIndex((i) => Math.max(i - 1, 0));
  const handleNext = () => setActiveIndex((i) => Math.min(i + 1, timelineSteps.length - 1));

  return (
    <Box sx={styles.howItWorksWrapper}>
      <Box sx={styles.howItWorksDarkOverlay} />
      <Box sx={styles.howItWorksTopGradient} />
      <Box sx={styles.howItWorksBottomGradient} />
      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 2 }}>
        <Box sx={{ textAlign: "center", mb: { xs: 8, md: 10 } }}>
          <Typography sx={styles.howItWorksEyebrow}>{t("howItWorks.eyebrow")}</Typography>
          <Typography variant="h2" sx={styles.howItWorksHeadline}>
            {t("howItWorks.headlineWhite")}
            <Box component="span" sx={styles.gradientText}>
              {t("howItWorks.headlineGradient")}
            </Box>
          </Typography>
          <Typography sx={styles.howItWorksSubheading}>{t("howItWorks.subtitle")}</Typography>
        </Box>

        <Box sx={styles.howItWorksCarouselWrapper}>
          <IconButton
            onClick={handlePrev}
            disabled={activeIndex === 0}
            sx={{
              ...styles.howItWorksPrevButton,
              color: activeIndex === 0 ? "rgba(255,255,255,0.2)" : "#38bdf8",
              borderColor: activeIndex === 0 ? "rgba(255,255,255,0.1)" : "rgba(56,189,248,0.4)",
              boxShadow: activeIndex === 0 ? "none" : "0 0 12px rgba(56,189,248,0.35), 0 0 24px rgba(56,189,248,0.15)",
            }}
          >
            <ArrowBackIosNewIcon sx={{ fontSize: "1rem" }} />
          </IconButton>

          <Box sx={styles.howItWorksCarouselViewport}>
            {timelineSteps.map((step, index) => {
              const position = index - activeIndex;
              const isVisible = Math.abs(position) <= 1;
              const isCenter = position === 0;

              return (
                <Box
                  key={step.number}
                  sx={{
                    ...styles.howItWorksCardBase,
                    opacity: (() => {
                      if (isCenter) return 1;
                      return isVisible ? 0.65 : 0;
                    })(),
                    transform: `translateX(${position * 105}%) scale(${isCenter ? 1 : 0.7})`,
                    pointerEvents: isCenter ? "auto" : "none",
                    zIndex: isCenter ? 2 : 1,
                  }}
                >
                  <Box
                    sx={{
                      bgcolor: isCenter ? "rgba(5,12,35,0.80)" : "rgba(4,9,28,0.82)",
                      backdropFilter: "blur(16px)",
                      WebkitBackdropFilter: "blur(16px)",
                      border: isCenter ? "1px solid rgba(56,189,248,0.6)" : "1px solid rgba(56,189,248,0.1)",
                      borderRadius: "20px",
                      p: { xs: "28px", md: "36px" },
                      boxShadow: isCenter
                        ? "0 8px 40px rgba(10,15,30,0.6), 0 0 60px rgba(56,189,248,0.25), 0 0 120px rgba(56,189,248,0.12)"
                        : "0 4px 20px rgba(10,15,30,0.4)",
                    }}
                  >
                    <Box
                      sx={{
                        color: isCenter ? "#38bdf8" : "rgba(255,255,255,0.25)",
                        fontSize: "2rem",
                        mb: 2,
                        display: "flex",
                        "& svg": { fontSize: "2rem" },
                      }}
                    >
                      {step.icon}
                    </Box>
                    <Typography
                      sx={{
                        ...styles.howItWorksStepNumber,
                        color: isCenter ? "rgba(56,189,248,0.7)" : "rgba(255,255,255,0.25)",
                      }}
                    >
                      {t("howItWorks.stepLabel")} {step.number}
                    </Typography>
                    <Typography sx={styles.howItWorksStepTitle}>{step.title}</Typography>
                    <Typography sx={styles.howItWorksStepDescription}>{step.description}</Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>

          <IconButton
            onClick={handleNext}
            disabled={activeIndex === timelineSteps.length - 1}
            sx={{
              ...styles.howItWorksNextButton,
              color: activeIndex === timelineSteps.length - 1 ? "rgba(255,255,255,0.2)" : "#38bdf8",
              borderColor: activeIndex === timelineSteps.length - 1 ? "rgba(255,255,255,0.1)" : "rgba(56,189,248,0.4)",
              boxShadow:
                activeIndex === timelineSteps.length - 1
                  ? "none"
                  : "0 0 12px rgba(56,189,248,0.35), 0 0 24px rgba(56,189,248,0.15)",
            }}
          >
            <ArrowForwardIosIcon sx={{ fontSize: "1rem" }} />
          </IconButton>
        </Box>

        <Box sx={styles.howItWorksDots}>
          {timelineSteps.map((step, index) => (
            <Box
              key={step.number}
              onClick={() => setActiveIndex(index)}
              sx={{
                width: index === activeIndex ? 24 : 8,
                height: 8,
                borderRadius: "4px",
                background: index === activeIndex ? "#38bdf8" : "rgba(255,255,255,0.2)",
                transition: "background 0.4s ease, width 0.4s ease",
                cursor: "pointer",
              }}
            />
          ))}
        </Box>

        <Box component="form" onSubmit={handleZipSubmit} sx={styles.howItWorksFormWrapper}>
          <Box
            component="input"
            placeholder={t("howItWorks.zipPlaceholder")}
            value={zip}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setZip(e.target.value)}
            sx={styles.howItWorksZipInput}
          />
          <Box component="button" type="submit" sx={styles.howItWorksSubmitButton}>
            {t("howItWorks.checkSavings")}
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

type TestimonialsSectionPropsT = {
  videoTestimonials: VideoTestimonialT[];
  t: TranslateFnT;
};

const TestimonialsSection = ({ videoTestimonials, t }: TestimonialsSectionPropsT) => {
  const { ref, inView } = useInView();
  const [featuredIndex, setFeaturedIndex] = useState(0);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const featuredVideo = videoTestimonials[featuredIndex];
  const thumbnailVideos = videoTestimonials.filter((_, i) => i !== featuredIndex);

  const handleThumbnailClick = (clickedId: number) => {
    const newIndex = videoTestimonials.findIndex((v) => v.id === clickedId);
    if (newIndex === featuredIndex) return;
    setVideoPlaying(false);
    setTransitioning(true);
    setTimeout(() => {
      setFeaturedIndex(newIndex);
      setTransitioning(false);
    }, 300);
  };

  return (
    <Box
      ref={ref}
      sx={{
        ...styles.testimonialsWrapper,
        opacity: inView ? 1 : 0,
        transition: "opacity 1s ease",
        zIndex: videoPlaying ? 1201 : undefined,
      }}
    >
      {videoPlaying && featuredVideo.vimeoUrl ? (
        <>
          <Box
            component="iframe"
            src={`${featuredVideo.vimeoUrl}${featuredVideo.vimeoUrl.includes("?") ? "&" : "?"}autoplay=1`}
            frameBorder={0}
            allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
            referrerPolicy="strict-origin-when-cross-origin"
            sx={styles.testimonialsIframe}
          />
          <Box onClick={() => setVideoPlaying(false)} sx={styles.testimonialsCloseButton}>
            <CloseIcon sx={{ color: "white", fontSize: 20 }} />
          </Box>
        </>
      ) : (
        <Box
          className="featured-bg"
          sx={{
            position: "absolute",
            inset: 0,
            background: featuredVideo.placeholder,
            opacity: transitioning ? 0 : 1,
            transition: "opacity 0.4s ease, transform 6s ease",
            transform: "scale(1.04)",
            "&:hover": { transform: "scale(1.06)" },
          }}
        />
      )}

      <Box sx={styles.testimonialsGradientBottom} />
      <Box sx={styles.testimonialsGradientLeft} />

      {!videoPlaying && (
        <Box onClick={() => setVideoPlaying(true)} sx={styles.testimonialsPlayOverlay}>
          <Box className="play-btn" sx={styles.testimonialsPlayButton}>
            <PlayArrowIcon sx={{ fontSize: { xs: 36, md: 46 }, color: "white" }} />
          </Box>
        </Box>
      )}

      <Box
        sx={{
          ...styles.testimonialsInfoPanel,
          opacity: transitioning ? 0 : 1,
          transition: "opacity 0.4s ease",
        }}
      >
        <Typography sx={styles.testimonialsEyebrow}>{t("testimonials.sectionEyebrow")}</Typography>
        <Typography sx={styles.testimonialsHeadline}>
          {t("testimonials.sectionHeadlineWhite1")}
          <br />
          {t("testimonials.sectionHeadlineWhite2")}
          <Box component="span" sx={{ ...styles.gradientText }}>
            {t("testimonials.sectionHeadlineGradient")}
          </Box>
        </Typography>
        <Typography sx={styles.testimonialsVideoLabel}>{featuredVideo.label}</Typography>
        <Typography sx={styles.testimonialsVideoDescription}>{featuredVideo.description}</Typography>
      </Box>

      <Box sx={styles.testimonialsThumbnailsPanel}>
        {thumbnailVideos.map((video) => (
          <Box key={video.id} onClick={() => handleThumbnailClick(video.id)} sx={styles.testimonialsThumbnail}>
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                background: video.placeholder,
              }}
            />
            <Box sx={styles.testimonialsThumbnailGradient} />
            <Box className="thumb-play" sx={styles.testimonialsThumbnailPlayOverlay}>
              <Box sx={styles.testimonialsThumbnailPlayIcon}>
                <PlayArrowIcon sx={{ fontSize: 18, color: "white" }} />
              </Box>
            </Box>
            <Box sx={{ position: "absolute", bottom: 10, left: 10, right: 10 }}>
              <Typography sx={styles.testimonialsThumbnailLabel}>{video.label}</Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

type CTASectionPropsT = {
  t: TranslateFnT;
};

const CTASection = ({ t }: CTASectionPropsT) => {
  const { user } = useParams<{ user?: string }>();
  const prefix = user ? `/${user}` : "";
  const { ref, inView } = useInView();
  return (
    <Box sx={styles.ctaWrapper}>
      <Box
        component="video"
        autoPlay
        muted
        loop
        playsInline
        src="/img/assets/Powur_SolarInstall (2).mp4"
        sx={styles.ctaVideo}
      />
      <Box sx={styles.ctaDarkOverlay} />
      <Box sx={styles.ctaRadialOverlay} />
      <Container maxWidth={false} sx={{ position: "relative", zIndex: 2, px: { xs: 4, md: 10 } }}>
        <div
          ref={ref}
          style={{
            opacity: inView ? 1 : 0,
            transform: inView ? "translateY(0)" : "translateY(32px)",
            transition: "opacity 0.8s ease, transform 0.8s ease",
          }}
        >
          <Typography sx={styles.ctaEyebrow}>{t("cta.eyebrow")}</Typography>
          <Typography variant="h2" sx={styles.ctaHeadline}>
            {t("cta.headlineWhite")}
            <br />
            <Box component="span" sx={{ ...styles.gradientText }}>
              {t("cta.headlineGradient")}
            </Box>
          </Typography>
          <Typography sx={styles.ctaBody}>{t("cta.body")}</Typography>
          <Box sx={{ display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
            <Button
              component={Link}
              to={`${prefix}/join`}
              variant="contained"
              size="large"
              color="primary"
              sx={{ borderRadius: "50px", px: 4, py: 1.5 }}
            >
              {t("cta.primaryLabel")}
            </Button>
            <Button
              component={Link}
              to={`${prefix}/join`}
              variant="outlined"
              size="large"
              sx={styles.ctaOutlinedButton}
            >
              {t("cta.secondaryLabel")}
            </Button>
          </Box>
        </div>
      </Container>
    </Box>
  );
};

const HomeLightContent = () => {
  const { t } = useTranslation("static_home_light");
  const productsRef = useRef<HTMLDivElement>(null);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quoteZip, setQuoteZip] = useState("");

  const handleOpenQuote = (zip: string) => {
    setQuoteZip(zip);
    setQuoteOpen(true);
  };

  const productSlides: ProductSlideT[] = [
    {
      eyebrow: t("products.powerwall.eyebrow"),
      productName: t("products.powerwall.productName"),
      headlineWhite: t("products.powerwall.headlineWhite"),
      headlineGradient: t("products.powerwall.headlineGradient"),
      body: t("products.powerwall.body"),
      imagePath: "/img/assets/TeslaPowerwall3.png",
      specs: [
        {
          number: t("products.powerwall.specs.capacity.number"),
          unit: t("products.powerwall.specs.capacity.unit"),
          label: t("products.powerwall.specs.capacity.label"),
          description: t("products.powerwall.specs.capacity.description"),
        },
        {
          number: t("products.powerwall.specs.continuousPower.number"),
          unit: t("products.powerwall.specs.continuousPower.unit"),
          label: t("products.powerwall.specs.continuousPower.label"),
          description: t("products.powerwall.specs.continuousPower.description"),
        },
        {
          number: t("products.powerwall.specs.efficiency.number"),
          unit: t("products.powerwall.specs.efficiency.unit"),
          label: t("products.powerwall.specs.efficiency.label"),
          description: t("products.powerwall.specs.efficiency.description"),
        },
      ],
    },
    {
      eyebrow: t("products.enphase.eyebrow"),
      productName: t("products.enphase.productName"),
      headlineWhite: t("products.enphase.headlineWhite"),
      headlineGradient: t("products.enphase.headlineGradient"),
      body: t("products.enphase.body"),
      imagePath: "/img/assets/Enphase-battery-2.png",
      specs: [
        {
          number: t("products.enphase.specs.perBattery.number"),
          unit: t("products.enphase.specs.perBattery.unit"),
          label: t("products.enphase.specs.perBattery.label"),
          description: t("products.enphase.specs.perBattery.description"),
        },
        {
          number: t("products.enphase.specs.continuousPower.number"),
          unit: t("products.enphase.specs.continuousPower.unit"),
          label: t("products.enphase.specs.continuousPower.label"),
          description: t("products.enphase.specs.continuousPower.description"),
        },
        {
          number: t("products.enphase.specs.warranty.number"),
          unit: t("products.enphase.specs.warranty.unit"),
          label: t("products.enphase.specs.warranty.label"),
          description: t("products.enphase.specs.warranty.description"),
        },
      ],
    },
    {
      eyebrow: t("products.qcells.eyebrow"),
      productName: t("products.qcells.productName"),
      headlineWhite: t("products.qcells.headlineWhite"),
      headlineGradient: t("products.qcells.headlineGradient"),
      headlineBreak: true,
      body: t("products.qcells.body"),
      imagePath: "/img/assets/QCells.png",
      specs: [
        {
          number: t("products.qcells.specs.efficiency.number"),
          unit: t("products.qcells.specs.efficiency.unit"),
          label: t("products.qcells.specs.efficiency.label"),
          description: t("products.qcells.specs.efficiency.description"),
        },
        {
          label: t("products.qcells.specs.weatherDurability.label"),
          description: t("products.qcells.specs.weatherDurability.description"),
          icon: <ThunderstormIcon sx={{ fontSize: 40 }} />,
        },
        {
          number: t("products.qcells.specs.warranty.number"),
          unit: t("products.qcells.specs.warranty.unit"),
          label: t("products.qcells.specs.warranty.label"),
          description: t("products.qcells.specs.warranty.description"),
        },
      ],
    },
  ];

  const videoTestimonials: VideoTestimonialT[] = [
    {
      id: 1,
      label: t("testimonials.videos.theBetterWay.label"),
      description: t("testimonials.videos.theBetterWay.description"),
      placeholder: `url(${videoThumb1}) center/cover no-repeat`,
      vimeoUrl:
        "https://player.vimeo.com/video/512576082?h=ee055a9a43&title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479",
    },
    {
      id: 2,
      label: t("testimonials.videos.energyIndependence.label"),
      description: t("testimonials.videos.energyIndependence.description"),
      placeholder: `url(${videoThumb2}) center/cover no-repeat`,
      vimeoUrl:
        "https://player.vimeo.com/video/512576225?h=99077e2e31&title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479",
    },
    {
      id: 3,
      label: t("testimonials.videos.servicesYouTrust.label"),
      description: t("testimonials.videos.servicesYouTrust.description"),
      placeholder: `url(${videoThumb3}) center/cover no-repeat`,
      vimeoUrl:
        "https://player.vimeo.com/video/512576700?h=b0fd48fa0a&title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479",
    },
  ];

  const technologyFeatures: TechnologyFeatureT[] = [
    {
      title: t("technology.features.satelliteRoof.title"),
      description: t("technology.features.satelliteRoof.description"),
    },
    {
      title: t("technology.features.realtimeDesign.title"),
      description: t("technology.features.realtimeDesign.description"),
    },
    {
      title: t("technology.features.planComparison.title"),
      description: t("technology.features.planComparison.description"),
    },
    {
      title: t("technology.features.eSignature.title"),
      description: t("technology.features.eSignature.description"),
    },
  ];

  const statCards: StatCardT[] = [
    {
      value: t("socialProof.stats.poweredHomes.value"),
      label: t("socialProof.stats.poweredHomes.label"),
      animationDelay: "0.5s",
    },
    {
      value: t("socialProof.stats.revenue.value"),
      label: t("socialProof.stats.revenue.label"),
      animationDelay: "0.65s",
    },
    {
      value: t("socialProof.stats.customerRating.value"),
      label: t("socialProof.stats.customerRating.label"),
      animationDelay: "0.8s",
    },
    {
      value: t("socialProof.stats.carbonSequestered.value"),
      label: t("socialProof.stats.carbonSequestered.label"),
      animationDelay: "0.95s",
    },
    {
      value: t("socialProof.stats.customerSavings.value"),
      label: t("socialProof.stats.customerSavings.label"),
      animationDelay: "1.1s",
    },
  ];

  const timelineSteps: TimelineStepT[] = [
    {
      number: t("howItWorks.steps.step01.number"),
      title: t("howItWorks.steps.step01.title"),
      description: t("howItWorks.steps.step01.description"),
      icon: <ReceiptLongIcon />,
    },
    {
      number: t("howItWorks.steps.step02.number"),
      title: t("howItWorks.steps.step02.title"),
      description: t("howItWorks.steps.step02.description"),
      icon: <DescriptionIcon />,
    },
    {
      number: t("howItWorks.steps.step03.number"),
      title: t("howItWorks.steps.step03.title"),
      description: t("howItWorks.steps.step03.description"),
      icon: <CheckCircleOutlineIcon />,
    },
    {
      number: t("howItWorks.steps.step04.number"),
      title: t("howItWorks.steps.step04.title"),
      description: t("howItWorks.steps.step04.description"),
      icon: <HistoryEduIcon />,
    },
    {
      number: t("howItWorks.steps.step05.number"),
      title: t("howItWorks.steps.step05.title"),
      description: t("howItWorks.steps.step05.description"),
      icon: <RocketLaunchIcon />,
    },
  ];

  return (
    <>
      <VideoHero onScrollToProducts={() => productsRef.current?.scrollIntoView({ behavior: "smooth" })} t={t} />
      <ProductsSection sectionRef={productsRef} productSlides={productSlides} />
      <SocialProofSection statCards={statCards} t={t} />
      <FinancingSection t={t} />
      <TechnologySection technologyFeatures={technologyFeatures} t={t} />
      <HowItWorksSection timelineSteps={timelineSteps} t={t} onQuoteOpen={handleOpenQuote} />
      <TestimonialsSection videoTestimonials={videoTestimonials} t={t} />
      <CTASection t={t} />
      <QuoteModal open={quoteOpen} zip={quoteZip} onClose={() => setQuoteOpen(false)} />
    </>
  );
};

const HomeLightPage = () => {
  const { t } = useTranslation("static_home_light");
  return (
    <StaticPageLayout title={t("meta.title")} noTopSpacer>
      <HomeLightContent />
    </StaticPageLayout>
  );
};

export default HomeLightPage;
